import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, Trash2, ArrowLeft, ShoppingBag, Truck, MapPin, User, Minus, Plus, Tag } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { checkRentEligibility } from '../lib/rentEligibility';
import { saveCheckoutResume } from '../lib/checkoutResume';
import RentCriteriaDialog from '../components/RentCriteriaDialog';
import { calculateRentalCharges } from '../lib/rentalPricing';

const SHIPPING_CHARGE = 59; // Shipping charge for doorstep delivery

const CartPage = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [deliveryMethod, setDeliveryMethod] = useState('pickup'); // 'delivery' or 'pickup'
  const [rentEligibility, setRentEligibility] = useState(null);
  const [showRentCriteria, setShowRentCriteria] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartItems(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartItems(cartItems.filter(item => item.id !== itemId));
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      toast.error('Please enter a coupon code');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/validate-coupon`,
        { code: couponCode, total_amount: calculateTotal() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAppliedCoupon(response.data);
      setDiscount(response.data.discount_amount);
      toast.success(`Coupon applied! You saved ₹${response.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
      setDiscount(0);
    }
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    try {
      const result = await checkRentEligibility(cartItems, token);
      if (!result?.ok) {
        const maxWeeks = Math.max(...cartItems.map(item => item.weeks || 1), 1);
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() + 1);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + maxWeeks * 7);
        const shortfall = Math.max(
          0,
          Number(result?.blockers?.[0]?.shortfall)
            || (Number(result?.required_deposit || 0) - Number(result?.deposit_balance || 0))
        );
        saveCheckoutResume({
          cartItems: cartItems.map(item => ({
            book_id: item.book_id,
            book: item.book,
            quantity: item.quantity || 1,
            weeks: item.weeks,
            discount_percentage: item.discount_percentage || 0,
            original_rental_price: item.original_rental_price || item.book.rental_price_per_week,
            discounted_rental_price: item.discounted_rental_price || item.book.rental_price_per_week,
          })),
          weeklyTotalAmount: calculateWeeklyTotal(),
          rentalStartDate: startDate.toISOString().split('T')[0],
          rentalEndDate: endDate.toISOString().split('T')[0],
          rentalDurationWeeks: maxWeeks,
          rentalDays: maxWeeks * 7,
          couponCode: appliedCoupon ? couponCode : '',
          discount,
          deliveryMethod,
          depositShortfall: shortfall,
        });
        setRentEligibility(result);
        setShowRentCriteria(true);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify rent criteria');
      return;
    }

    // Navigate to checkout page with cart data including coupon, discount, and delivery method
    const maxWeeks = Math.max(...cartItems.map(item => item.weeks || 1), 1);
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() + 1);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + maxWeeks * 7);

    navigate('/checkout', {
      state: {
        cartItems: cartItems.map(item => ({
          book_id: item.book_id,
          book: item.book,
          quantity: item.quantity || 1,
          weeks: item.weeks,
          discount_percentage: item.discount_percentage || 0,
          original_rental_price: item.original_rental_price || item.book.rental_price_per_week,
          discounted_rental_price: item.discounted_rental_price || item.book.rental_price_per_week,
        })),
        weeklyTotalAmount: calculateWeeklyTotal(),
        rentalStartDate: startDate.toISOString().split('T')[0],
        rentalEndDate: endDate.toISOString().split('T')[0],
        rentalDurationWeeks: maxWeeks,
        rentalDays: maxWeeks * 7,
        couponCode: appliedCoupon ? couponCode : '',
        discount: discount,
        deliveryMethod: deliveryMethod,
        shippingCharge: getShippingCharge()
      }
    });
  };

  const handleUpdateQuantity = async (itemId, newQty) => {
    try {
      await axios.patch(`${API}/cart/${itemId}/quantity`, { quantity: newQty }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (newQty < 1) {
        setCartItems(cartItems.filter(item => item.id !== itemId));
      } else {
        setCartItems(cartItems.map(item => item.id === itemId ? {...item, quantity: newQty} : item));
      }
      window.dispatchEvent(new Event('cart-updated'));
    } catch { toast.error('Failed to update quantity'); }
  };

  const calculateWeeklyTotal = () => {
    return cartItems.reduce((sum, item) => {
      const price = item.discounted_rental_price || item.book.rental_price_per_week;
      const qty = item.quantity || 1;
      return sum + (price * qty);
    }, 0);
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => {
      const price = item.discounted_rental_price || item.book.rental_price_per_week;
      const qty = item.quantity || 1;
      return sum + (price * item.weeks * qty);
    }, 0);
  };

  const calculateOriginalTotal = () => {
    return cartItems.reduce((sum, item) => {
      const qty = item.quantity || 1;
      return sum + (item.book.rental_price_per_week * item.weeks * qty);
    }, 0);
  };

  const totalCategoryDiscount = calculateOriginalTotal() - calculateTotal();

  const getShippingCharge = () => {
    return deliveryMethod === 'delivery' ? SHIPPING_CHARGE : 0;
  };

  const getRentalDays = () => Math.max(...cartItems.map(item => (item.weeks || 1) * 7), 7);

  const getRentalSubtotal = () => {
    const rentalDays = getRentalDays();
    return Math.round((calculateWeeklyTotal() / 7) * rentalDays);
  };

  const getChargeBreakdown = () => calculateRentalCharges(getRentalSubtotal());

  const calculateFinalAmount = () => {
    const { platformFee, gst } = getChargeBreakdown();
    return getRentalSubtotal() + platformFee + gst - discount + getShippingCharge();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-8" style={{color: 'var(--foreground)'}} data-testid="cart-title">Shopping Cart</h1>

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2" data-testid="empty-cart-message">Your cart is empty</h2>
            <p className="text-gray-500 mb-6">Add some books to get started!</p>
            <Button
              onClick={() => navigate('/')}
              style={{backgroundColor: 'var(--primary)', color: 'white'}}
              className="hover:opacity-90"
              data-testid="browse-books-button"
            >
              Browse Books
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl shadow-md p-6 flex items-center space-x-6" data-testid={`cart-item-${item.id}`}>
                  <div className="w-24 h-32 rounded-lg flex items-center justify-center flex-shrink-0 relative" style={{backgroundColor: '#f3ece0'}}>
                    {item.book.cover_image ? (
                      <img src={item.book.cover_image} alt={item.book.title} className="h-full w-full object-contain rounded-lg p-1" />
                    ) : (
                      <BookOpen className="h-12 w-12" style={{color: 'var(--primary)'}} />
                    )}
                    {item.discount_percentage > 0 && (
                      <span className="absolute top-1 left-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{item.discount_percentage}% OFF</span>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1" style={{color: 'var(--foreground)'}} data-testid={`cart-item-title-${item.id}`}>{item.book.title}</h3>
                    <p className="text-gray-600 mb-2">{item.book.author}</p>
                    <p className="text-sm text-gray-500">Duration: {item.weeks} week{item.weeks > 1 ? 's' : ''}</p>
                    <div className="mt-2 flex items-center gap-3">
                      {item.discount_percentage > 0 ? (
                        <>
                          <span className="text-sm text-gray-400 line-through">₹{Math.round(item.original_rental_price * item.weeks * (item.quantity || 1))}</span>
                          <span className="text-xl font-bold" style={{color: 'var(--accent)'}} data-testid={`cart-item-price-${item.id}`}>
                            ₹{Math.round(item.discounted_rental_price * item.weeks * (item.quantity || 1))}
                          </span>
                          <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">{item.discount_percentage}% off</span>
                        </>
                      ) : (
                        <span className="text-xl font-bold" style={{color: 'var(--accent)'}} data-testid={`cart-item-price-${item.id}`}>
                          ₹{Math.round(item.book.rental_price_per_week * item.weeks * (item.quantity || 1))}
                        </span>
                      )}
                      <span className="text-sm text-gray-500">
                        (₹{Math.round(item.discounted_rental_price || item.book.rental_price_per_week)}/week)
                      </span>
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2" data-testid={`cart-qty-controls-${item.id}`}>
                    <button
                      onClick={() => handleUpdateQuantity(item.id, (item.quantity || 1) - 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                      data-testid={`qty-minus-${item.id}`}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center font-semibold" data-testid={`qty-value-${item.id}`}>{item.quantity || 1}</span>
                    <button
                      onClick={() => handleUpdateQuantity(item.id, (item.quantity || 1) + 1)}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
                      data-testid={`qty-plus-${item.id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={() => handleRemoveItem(item.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-item-button-${item.id}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
                <h2 className="text-2xl font-bold mb-6" style={{color: 'var(--foreground)'}}>Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-700">
                    <span>Rental amount ({getRentalDays()} days)</span>
                    <span className="font-semibold" data-testid="subtotal-amount">₹{getRentalSubtotal()}</span>
                  </div>
                  <div className="flex justify-between text-gray-700" data-testid="platform-fee-line">
                    <span>Platform fee</span>
                    <span className="font-semibold">₹{getChargeBreakdown().platformFee}</span>
                  </div>
                  <div className="flex justify-between text-gray-700" data-testid="gst-line">
                    <span>GST</span>
                    <span className="font-semibold">₹{getChargeBreakdown().gst}</span>
                  </div>
                  {totalCategoryDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" />Category Discount</span>
                      <span className="font-semibold">-₹{Math.round(totalCategoryDiscount)}</span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount Applied</span>
                      <span className="font-semibold">-₹{Math.round(discount)}</span>
                    </div>
                  )}
                  {deliveryMethod === 'delivery' && (
                    <div className="flex justify-between text-gray-700">
                      <span className="flex items-center">
                        <Truck className="h-4 w-4 mr-1" />
                        Shipping Charges
                      </span>
                      <span className="font-semibold">₹{SHIPPING_CHARGE}</span>
                    </div>
                  )}
                  {appliedCoupon && (
                    <div className="flex justify-between items-center p-2 rounded" style={{backgroundColor: 'var(--card)'}}>
                      <span className="text-sm font-medium" style={{color: 'var(--accent)'}}>
                        Coupon: {appliedCoupon.code} ({appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : `₹${appliedCoupon.discount_value}`})
                      </span>
                      <button onClick={() => { setAppliedCoupon(null); setDiscount(0); setCouponCode(''); }} className="text-red-600 text-sm">Remove</button>
                    </div>
                  )}
                  <div className="border-t pt-4 flex justify-between text-lg font-bold text-gray-900">
                    <span>Total Amount</span>
                    <span style={{color: 'var(--accent)'}} data-testid="total-amount">₹{Math.round(calculateFinalAmount())}</span>
                  </div>
                </div>

                {/* Delivery Method Selection */}
                <div className="mb-6 p-4 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Delivery Method
                  </label>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setDeliveryMethod('pickup')}
                      className={`w-full p-3 rounded-lg border-2 flex items-center justify-between transition-all ${
                        deliveryMethod === 'pickup' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white'
                      }`}
                      data-testid="pickup-option"
                    >
                      <div className="flex items-center">
                        <MapPin className="h-5 w-5 mr-3" style={{color: deliveryMethod === 'pickup' ? 'var(--accent)' : '#6b7280'}} />
                        <div className="text-left">
                          <p className="font-semibold">Self Pickup</p>
                          <p className="text-xs text-gray-500">Collect from book owner</p>
                        </div>
                      </div>
                      <span className="font-bold text-green-600">FREE</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setDeliveryMethod('delivery')}
                      className={`w-full p-3 rounded-lg border-2 flex items-center justify-between transition-all ${
                        deliveryMethod === 'delivery' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white'
                      }`}
                      data-testid="delivery-option"
                    >
                      <div className="flex items-center">
                        <Truck className="h-5 w-5 mr-3" style={{color: deliveryMethod === 'delivery' ? 'var(--accent)' : '#6b7280'}} />
                        <div className="text-left">
                          <p className="font-semibold">Home Delivery</p>
                          <p className="text-xs text-gray-500">Shiprocket to your address</p>
                        </div>
                      </div>
                      <span className="font-bold text-amber-600">+₹{SHIPPING_CHARGE}</span>
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Have a Coupon Code?
                  </label>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                      data-testid="coupon-input"
                      disabled={appliedCoupon !== null}
                    />
                    <Button 
                      onClick={handleApplyCoupon} 
                      disabled={appliedCoupon !== null}
                      style={{backgroundColor: 'var(--primary)', color: 'white'}}
                      className="hover:opacity-90"
                      data-testid="apply-coupon-button"
                    >
                      Apply
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  className="w-full h-12 text-lg hover:opacity-90"
                  style={{backgroundColor: 'var(--accent)', color: 'white'}}
                  data-testid="checkout-button"
                >
                  Proceed to Checkout - ₹{Math.round(calculateFinalAmount())}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Secure payment processing
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <RentCriteriaDialog
        open={showRentCriteria}
        onOpenChange={setShowRentCriteria}
        eligibility={rentEligibility}
      />
      <Footer />
    </div>
  );
};

export default CartPage;